<fw-dialog width="medium">
  <fw-dialog-header>
    <h3 class="vision-h3">Gear Access <span class="gear-name-title">• {{ gearLabel() }}</span></h3>
  </fw-dialog-header>
  <fw-dialog-content class="fill-width">
    @if (isLoading()) {
      <fw-progress-bar mode="indeterminate"></fw-progress-bar>
    }
    <form [formGroup]="form">
      @if (isRestricted() && (isDeveloper() || isSiteAdmin())) {
        <fw-tabs [activeTabIndex]="0" [bordered]="true">
          <fw-tab tabindex="projects" [panel]="projectsPanel" [title]="'Projects'">
            <fw-badge test-id="selected-projects-count"
              variant="static"
              [value]="selectedProjectLabels().length.toString()"
              [fwTooltip]="selectedProjectLabels().join('\n')">
            </fw-badge>
          </fw-tab>
          <fw-tab tabindex="users" [panel]="usersPanel" [title]="'Users'">
            <fw-badge test-id="selected-users-count"
              variant="static"
              [value]="selectedUserLabels().length.toString()"
              [fwTooltip]="selectedUserLabels().join('\n')">
            </fw-badge>
          </fw-tab>
        </fw-tabs>

        <fw-tab-panel #projectsPanel>
          <div class="projects-panel-content">
            <div class="picker-row">
              <fw-multi-select
                #projectPicker
                width="100%"
                test-id="project-picker-select"
                placeholder="Add projects..."
                [closeOnSelect]="true"
                [maxSelectedShown]="5"
                [formControl]="projectPickerControl"
                (keydown)="$event.stopPropagation()"
                (click)="$event.stopPropagation()"
              >
                @for (project of availableProjects(); track project._id) {
                  <fw-menu-item
                    [value]="project._id"
                    [title]="project.label"
                    [description]="groupLabels()[project.group]">
                  </fw-menu-item>
                }
              </fw-multi-select>
              <fw-button test-id="add-projects-button" [disabled]="!projectPickerControl.value?.length" (click)="addSelectedProjects()">Add</fw-button>
            </div>

            @if (addedProjects().length > 0) {
              <fw-section-heading title="Projects with access"></fw-section-heading>
              <fw-table #addedProjectsTable test-id="added-projects-table" [dataSource]="addedProjects()"
                [class.scrollable]="isAddedProjectsTableScrollable()">
                <ng-container matColumnDef="label">
                  <th fw-header-cell *matHeaderCellDef>Project</th>
                  <td fw-cell *matCellDef="let project">{{ project.label }}</td>
                </ng-container>

                <ng-container matColumnDef="group">
                  <th fw-header-cell *matHeaderCellDef>Group</th>
                  <td fw-cell *matCellDef="let project">{{ groupLabels()[project.group] }}</td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th fw-header-cell class="row-trash-column" *matHeaderCellDef></th>
                  <td fw-cell *matCellDef="let project" class="row-trash-column">
                    <fw-icon-button icon="trash-delete"
                      test-id="remove-added-project-btn"
                      (click)="removeAddedProject(project._id)">
                    </fw-icon-button>
                  </td>
                </ng-container>

                <tr fw-header-row *matHeaderRowDef="addedProjectsColumns; sticky: true"></tr>
                <tr fw-row *matRowDef="let row; columns: addedProjectsColumns;"></tr>
              </fw-table>
            }
          </div>
        </fw-tab-panel>

        <fw-tab-panel #usersPanel>
          <div class="users-panel-content">
            <div class="picker-row">
              <fw-multi-select
                #userPicker
                width="100%"
                test-id="user-picker-select"
                placeholder="Add users..."
                [closeOnSelect]="false"
                [maxSelectedShown]="5"
                [formControl]="userPickerControl"
                [filterFn]="userPickerFilterFn"
                (keydown)="$event.stopPropagation()"
                (click)="$event.stopPropagation()"
              >
                @for (user of availableUsers(); track user._id) {
                  <fw-menu-item
                    [value]="user._id"
                    [title]="user.firstname + ' ' + user.lastname"
                    [description]="user.email"
                  >
                    <fw-avatar  size="medium"
                      variant="circular"
                      [colorSeed]="user | userInitial"
                      [content]="user | userAvatarContent"
                      [imageUrl]="user | userAvatarUrl"
                      [initial]="user | userInitial"
                    />
                  </fw-menu-item>
                }
              </fw-multi-select>
              <fw-button test-id="add-users-button" [disabled]="!userPickerControl.value?.length" (click)="addSelectedUsers()">Add</fw-button>
            </div>

            @if (addedUsers().length > 0) {
              <fw-section-heading title="Users with access"></fw-section-heading>
              <fw-table #addedUsersTable test-id="added-users-table" [dataSource]="addedUsers()"
                [class.scrollable]="isAddedUsersTableScrollable()">
                <ng-container matColumnDef="name">
                  <th fw-header-cell *matHeaderCellDef>Name</th>
                  <td fw-cell *matCellDef="let user">
                    <fw-avatar size="medium" variant="circular"
                      [content]="user | userAvatarContent"
                      [colorSeed]="user | userInitial"
                      [imageUrl]="user | userAvatarUrl"
                      [initial]="user | userInitial"
                    ></fw-avatar>
                    {{ user.firstname }} {{ user.lastname }}
                  </td>
                </ng-container>

                <ng-container matColumnDef="email">
                  <th fw-header-cell *matHeaderCellDef>Email</th>
                  <td fw-cell *matCellDef="let user">{{ user.email }}</td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th fw-header-cell class="row-trash-column" *matHeaderCellDef></th>
                  <td fw-cell *matCellDef="let user" class="row-trash-column">
                    <fw-icon-button icon="trash-delete"
                      test-id="remove-added-user-btn"
                      (click)="removeAddedUser(user._id)">
                    </fw-icon-button>
                  </td>
                </ng-container>

                <tr fw-header-row *matHeaderRowDef="addedUsersColumns; sticky: true"></tr>
                <tr fw-row *matRowDef="let row; columns: addedUsersColumns;"></tr>
              </fw-table>
            }
          </div>
        </fw-tab-panel>
      }

      <div class="restrict-access">
        <fw-contained-input description="Turning on restricted access allows you to limit a gear to specific groups,
            projects, and users you define. Turning off restricted access will open the gear to everyone on your site"
          formControlName="isRestricted"
          title="Restricted Access"
          test-id="gear-access-restrict-access-switch"
          [status]="isRestricted() ? 'ON' : 'OFF'"
        >
          <fw-avatar variant="rounded" icon="lock" size="medium"/>
        </fw-contained-input>
      </div>
    </form>
  </fw-dialog-content>
  <fw-dialog-actions class="fill-width">
    @if (hasChanged() && !isLoading()) {
      <fw-button color="primary"
        data-class="reset-button"
        test-id="reset-button"
        variant="outline"
        [disabled]="isSaving()"
        (click)="reset()">Reset Changes
      </fw-button>
      <fw-button variant="outline" color="primary" [disabled]="isSaving()" test-id="cancel-button" (click)="ref.close()">
        Cancel
      </fw-button>
      <fw-button color="primary" [variant]="isSaving() ? 'progress' : 'solid'" test-id="save-button" (click)="save()">
        {{ isSaving() ? 'Saving...' : 'Save' }}
      </fw-button>
    }
    @if (!hasChanged() || isLoading()) {
      <fw-button color="primary" test-id="close-button" (click)="ref.close()">Close</fw-button>
    }
  </fw-dialog-actions>
</fw-dialog>
